import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Grid,
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
} from "@mui/material";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { getStockAdjustmentsDetails } from "../../../../../config/Services";
import Spinner from "../../../../../components/Spinner";

const AdjustmentDetailsModal = ({ open, handleClose, adjustmentId }) => {
  const [detailsData, setDetailsData] = useState(null);
  const [loading, setLoading] = useState(false);

  const locId = localStorage.getItem("locId");
  const orgId = localStorage.getItem("orgId");

  // Fetch adjustment details from API
  const fetchAdjustmentDetails = async () => {
    if (!adjustmentId) return;
    const payload = {
      locationId: locId,
      orgId: orgId,
      gtin: adjustmentId?.barcode,
      adjustmentId: adjustmentId?.id,
      batchNo: adjustmentId?.batch,
    };

    setLoading(true);
    try {
      const response = await getStockAdjustmentsDetails(payload);

      const result = response?.data?.data?.data?.product[0];

      if (response?.data?.data?.es === 0 && response?.data?.data?.statusCode === 200) {
        setDetailsData(result);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && adjustmentId) {
      fetchAdjustmentDetails();
    }
  }, [open, adjustmentId]);

  console.log(detailsData);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB"); // DD/MM/YYYY format
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: "8px",
          padding: "0",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 24px",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Adjustment Details
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: "#666",
            "&:hover": { backgroundColor: "#f5f5f5" },
          }}
        >
          <XMarkIcon style={{ width: 20, height: 20 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "24px" }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "200px",
            }}
          >
            <Spinner />
          </Box>
        ) : detailsData ? (
          <>
            {/* Main Details Section */}
            <Grid
              container
              spacing={2}
              sx={{
                marginBottom: "24px"
              }}
            >
              <Grid item xs={12} sm={4}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#666",
                    fontSize: "12px",
                    display: "block",
                    marginBottom: "4px",
                  }}
                >
                  Adjusted Quantity
                </Typography>
                <TextField
                  value={detailsData?.adjustedQuantity || 0}
                  fullWidth
                  disabled
                  size="small"
                  InputProps={{
                    style: {
                      backgroundColor: "#f9f9f9",
                      fontSize: "14px",
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#d0d0d0",
                        borderWidth: "1px",
                      },
                      "&:hover fieldset": {
                        borderColor: "#d0d0d0",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#d0d0d0",
                      },
                      "&.Mui-disabled fieldset": {
                        borderColor: "#d0d0d0",
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#666",
                    fontSize: "12px",
                    display: "block",
                    marginBottom: "4px",
                  }}
                >
                  Adjusted Value
                </Typography>
                <TextField
                  value={detailsData?.adjustedValue || 0}
                  fullWidth
                  disabled
                  size="small"
                  InputProps={{
                    style: {
                      backgroundColor: "#f9f9f9",
                      fontSize: "14px",
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#d0d0d0",
                        borderWidth: "1px",
                      },
                      "&:hover fieldset": {
                        borderColor: "#d0d0d0",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#d0d0d0",
                      },
                      "&.Mui-disabled fieldset": {
                        borderColor: "#d0d0d0",
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#666",
                    fontSize: "12px",
                    display: "block",
                    marginBottom: "4px",
                  }}
                >
                  Adjusted By
                </Typography>
                <TextField
                  value={detailsData?.adjustedBy || "N/A"}
                  fullWidth
                  disabled
                  size="small"
                  InputProps={{
                    style: {
                      backgroundColor: "#f9f9f9",
                      fontSize: "14px",
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#d0d0d0",
                        borderWidth: "1px",
                      },
                      "&:hover fieldset": {
                        borderColor: "#d0d0d0",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#d0d0d0",
                      },
                      "&.Mui-disabled fieldset": {
                        borderColor: "#d0d0d0",
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#666",
                    fontSize: "12px",
                    display: "block",
                    marginBottom: "4px",
                  }}
                >
                  Batch ID 1
                </Typography>
                <TextField
                  value={detailsData?.batch || "N/A"}
                  fullWidth
                  disabled
                  size="small"
                  InputProps={{
                    style: {
                      backgroundColor: "#f9f9f9",
                      fontSize: "14px",
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#d0d0d0",
                        borderWidth: "1px",
                      },
                      "&:hover fieldset": {
                        borderColor: "#d0d0d0",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#d0d0d0",
                      },
                      "&.Mui-disabled fieldset": {
                        borderColor: "#d0d0d0",
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#666",
                    fontSize: "12px",
                    display: "block",
                    marginBottom: "4px",
                  }}
                >
                  Adjustment Reason
                </Typography>
                <TextField
                  value={detailsData?.reason || "N/A"}
                  fullWidth
                  disabled
                  size="small"
                  InputProps={{
                    style: {
                      backgroundColor: "#f9f9f9",
                      fontSize: "14px",
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#d0d0d0",
                        borderWidth: "1px",
                      },
                      "&:hover fieldset": {
                        borderColor: "#d0d0d0",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#d0d0d0",
                      },
                      "&.Mui-disabled fieldset": {
                        borderColor: "#d0d0d0",
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#666",
                    fontSize: "12px",
                    display: "block",
                    marginBottom: "4px",
                  }}
                >
                  Adjustment Date
                </Typography>
                <TextField
                  value={formatDate(detailsData?.lastUpdated)}
                  fullWidth
                  disabled
                  size="small"
                  InputProps={{
                    style: {
                      backgroundColor: "#f9f9f9",
                      fontSize: "14px",
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#d0d0d0",
                        borderWidth: "1px",
                      },
                      "&:hover fieldset": {
                        borderColor: "#d0d0d0",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#d0d0d0",
                      },
                      "&.Mui-disabled fieldset": {
                        borderColor: "#d0d0d0",
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>

            {/* Source Detail Section */}
            <Box sx={{ marginBottom: "24px" }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  marginBottom: "12px",
                  fontSize: "14px",
                }}
              >
                Source Detail
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#666",
                      fontSize: "12px",
                      display: "block",
                      marginBottom: "4px",
                    }}
                  >
                    Source
                  </Typography>
                  <FormControl fullWidth size="small">
                    <TextField
                      value={detailsData?.source || "N/A"}
                      fullWidth
                      disabled
                      size="small"
                      InputProps={{
                        style: {
                          backgroundColor: "#f9f9f9",
                          fontSize: "14px",
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#d0d0d0",
                            borderWidth: "1px",
                          },
                          "&:hover fieldset": {
                            borderColor: "#d0d0d0",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#d0d0d0",
                          },
                          "&.Mui-disabled fieldset": {
                            borderColor: "#d0d0d0",
                          },
                        },
                      }}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#666",
                      fontSize: "12px",
                      display: "block",
                      marginBottom: "4px",
                    }}
                  >
                    Source Id
                  </Typography>
                  <FormControl fullWidth size="small">
                    <TextField
                      value={detailsData?.sourceId || "N/A"}
                      fullWidth
                      disabled
                      size="small"
                      InputProps={{
                        style: {
                          backgroundColor: "#f9f9f9",
                          fontSize: "14px",
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#d0d0d0",
                            borderWidth: "1px",
                          },
                          "&:hover fieldset": {
                            borderColor: "#d0d0d0",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#d0d0d0",
                          },
                          "&.Mui-disabled fieldset": {
                            borderColor: "#d0d0d0",
                          },
                        },
                      }}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </Box>

            {/* Added Images Section */}
            {detailsData?.imgUrl && (
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    marginBottom: "12px",
                    fontSize: "14px",
                  }}
                >
                  Added Images
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: "12px",
                    flexWrap: "wrap",
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: "80px",
                      height: "80px",
                      borderRadius: "8px",
                      overflow: "hidden",
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <img
                      src={detailsData?.imgUrl}
                      alt="Product adjustment"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            )}
          </>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "200px",
            }}
          >
            <Typography>No data available</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AdjustmentDetailsModal;